/**
 * File: deviceChecksUtils.ts
 * Path: apps/admin/src/features/diagnostics/utils/deviceChecksUtils.ts
 * Description: The "This device" half of System > Diagnostics: everything
 * between Google and the notification on this screen that can break, from
 * the connection to the service worker to whether the server knows the
 * device. Pure: the facts are read by readDeviceFacts, the verdicts here.
 */

import type { DiagnosticCheck, PushDevice } from "@idk/config";
import { agoText } from "@idk/config";
import { isAppleMobile, type BrowserFacts } from "@/shared/utils/deviceUtils";
import { deviceHealth } from "@/shared/utils/notificationsUtils";

export interface DeviceFacts extends BrowserFacts {
  online: boolean;
  /** The admin's service worker: none, still installing, or running. */
  worker: "none" | "installing" | "active";
  /** The worker is in charge of this page (false until a reload after it first installs). */
  workerControlling: boolean;
  /** The browser holds a push subscription for this site; null when it can't be asked. */
  subscribed: boolean | null;
  /** This device as the server knows it, when it is set up. */
  thisDevice: PushDevice | null;
  /** When this copy of the admin was built; null on the dev server. */
  builtAt: string | null;
}

const SETUP = "Settings > Notifications > Turn on.";

export const deviceChecks = (facts: DeviceFacts, now: number): DiagnosticCheck[] => {
  const checks: DiagnosticCheck[] = [];
  const apple = isAppleMobile(facts);

  checks.push(
    facts.online
      ? { id: "online", label: "Internet connection", status: "ok", detail: "Online." }
      : {
          id: "online",
          label: "Internet connection",
          status: "fail",
          detail: "This device is offline. Nothing loads or arrives until it reconnects.",
          fix: "Check Wi-Fi or mobile data.",
        },
  );

  checks.push(
    facts.installed
      ? { id: "installed", label: "Installed app", status: "ok", detail: "Running as the installed app." }
      : apple
        ? {
            id: "installed",
            label: "Installed app",
            status: "fail",
            detail: "Running in Safari. Apple only allows notifications from the Home Screen app.",
            fix: "Safari > Share > Add to Home Screen, then open IDK Admin from the Home Screen.",
          }
        : {
            id: "installed",
            label: "Installed app",
            status: "info",
            detail: "Running in a browser tab. Notifications work here too; installing is optional.",
          },
  );

  checks.push(
    facts.configured
      ? { id: "key", label: "Web Push key", status: "ok", detail: "Included in this version of the admin." }
      : {
          id: "key",
          label: "Web Push key",
          status: "fail",
          detail: "This version of the admin was built without the Web Push key, so no device can switch notifications on.",
          fix: "Add VITE_FIREBASE_VAPID_KEY to the root .env and deploy the admin again.",
        },
  );

  const supported = facts.hasServiceWorker && facts.hasPushManager && facts.hasNotification;
  if (!supported && !(apple && !facts.installed)) {
    checks.push({
      id: "support",
      label: "Browser support",
      status: "fail",
      detail: "This browser can't receive notifications.",
      fix: "Use Chrome, Edge, Firefox or Safari, or the Home Screen app on an iPhone.",
    });
  }

  if (facts.permission !== null) {
    checks.push(
      facts.permission === "granted"
        ? { id: "permission", label: "Permission", status: "ok", detail: "Notifications are allowed for this site." }
        : facts.permission === "denied"
          ? {
              id: "permission",
              label: "Permission",
              status: "fail",
              detail: "Notifications are blocked for this site.",
              fix: apple
                ? "iPhone Settings > Notifications > IDK Admin > Allow Notifications."
                : "Allow them in the browser's site settings (the icon next to the address), then reload.",
            }
          : {
              id: "permission",
              label: "Permission",
              status: "warn",
              detail: "The browser hasn't been asked yet.",
              fix: SETUP,
            },
    );
  }

  if (facts.hasServiceWorker && facts.builtAt === null && facts.worker === "none") {
    checks.push({
      id: "worker",
      label: "Background worker",
      status: "info",
      detail: "Development build: the worker only runs in the deployed admin.",
    });
  } else if (facts.hasServiceWorker) {
    checks.push(
      facts.worker === "active" && facts.workerControlling
        ? { id: "worker", label: "Background worker", status: "ok", detail: "Running. It shows notifications and the offline screen." }
        : facts.worker === "none"
          ? {
              id: "worker",
              label: "Background worker",
              status: "fail",
              detail: "Not installed, so notifications can't be shown.",
              fix: "Reload the page. If it stays like this, clear this site's data in the browser and sign in again.",
            }
          : {
              id: "worker",
              label: "Background worker",
              status: "warn",
              detail: facts.worker === "installing" ? "Still installing." : "Installed, but not running this page yet.",
              fix: "Reload the page.",
            },
    );
  }

  const device = facts.thisDevice;
  if (facts.permission === "granted" && facts.subscribed === false) {
    checks.push({
      id: "subscription",
      label: "Push subscription",
      status: "fail",
      detail: "The browser dropped this device's push subscription, so notifications can't arrive.",
      fix: "Settings > Notifications > Turn off here, then Turn on again.",
    });
  } else if (facts.subscribed === true) {
    checks.push({ id: "subscription", label: "Push subscription", status: "ok", detail: "The browser holds a push subscription for the admin." });
  }

  if (!device) {
    checks.push({
      id: "registered",
      label: "Set up for notifications",
      status: facts.permission === "denied" ? "fail" : "warn",
      detail: "This device isn't set up, so it gets no notifications. They still collect in the bell.",
      fix: SETUP,
    });
  } else {
    const health = deviceHealth(device, now);
    const confirmed = device.lastConfirmedAt ? `last received a notification ${agoText(device.lastConfirmedAt, now)}` : "hasn't received a notification yet";
    checks.push(
      health === "ok"
        ? { id: "registered", label: "Set up for notifications", status: "ok", detail: `Set up as "${device.label}", ${confirmed}.` }
        : {
            id: "registered",
            label: "Set up for notifications",
            status: "warn",
            detail: `Set up as "${device.label}", but it ${health === "never" ? "has never confirmed a notification it was sent" : "stopped confirming notifications"}.`,
            fix: "Use the live test below. If nothing appears, Turn off here and Turn on again in Settings > Notifications.",
          },
    );
  }

  checks.push({
    id: "version",
    label: "Admin version",
    status: "info",
    detail: facts.builtAt
      ? `Built ${new Date(facts.builtAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}. Close and reopen the app to get a newer one.`
      : "Development build.",
  });

  return checks;
};
